import React from "react";
import { Link } from "react-router-dom";
import SubscribeFormGB from "@/components/SubscribeFormGB";
import PickupFormGB from "@/components/PickupFormGB";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function HomePage() {
  React.useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => scrollTo(id), 60);
    }
  }, []);

  return (
    <>
      {/* HERO */}
      <section id="top" className="hero">
        <div className="container-gb hero-grid">
          <div>
            <p className="status mono"><span className="dot" aria-hidden /> Első adag: készül 🌱</p>
            <h1 className="h-xl">Ami helyben keletkezik, <em>helyben ad új életet.</em></h1>
            <p className="sub">Helyi növényi alapanyagból, helyi komposztálással, a XVII. kerület kertjeinek.</p>
            <p className="body">A GrowBag egy helyi körforgásos kezdeményezés. Felhasználható növényi alapanyagból komposztalapú terméket készítünk, amelyet később visszaforgatunk a környék kertjeibe.</p>
            <div className="cta-row">
              <a href="#feliratkozas" onClick={(e) => { e.preventDefault(); scrollTo("feliratkozas"); }} className="btn btn-primary" data-testid="hero-cta-subscribe-button">
                Érdekel az első GrowBag <span className="arrow">→</span>
              </a>
              <a href="#hogyan-mukodik" onClick={(e) => { e.preventDefault(); scrollTo("hogyan-mukodik"); }} className="btn btn-ghost" data-testid="hero-cta-howitworks">
                Hogyan működik?
              </a>
            </div>
          </div>
          <div className="bag-stage" aria-hidden>
            <div className="bag">
              <span className="bag-seam left" />
              <span className="bag-seam right" />
              <span className="bag-hem" />
              <div className="bag-label">
                <div className="bl-word">GROWBAG</div>
                <div className="bl-rows">
                  <div className="bl-row"><span>Tétel</span><b>001</b></div>
                  <div className="bl-row"><span>Eredet</span><b>XVII. ker.</b></div>
                  <div className="bl-row"><span>Alapanyag</span><b>Növényi</b></div>
                  <div className="bl-row"><span>Állapot</span><b>Érlelés</b></div>
                </div>
              </div>
              <div className="bag-cut">
                <span className="cut-text">Itt kivágható<br />ide ültess</span>
              </div>
              <div className="bag-stamp"><div>Helyi<b>körforgás</b>zsákban</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker" aria-hidden>
        <div className="ticker-track">
          {Array.from({ length: 2 }).flatMap((_, k) => [
            <span key={`a${k}`}>Ne dobd ki. Növeszd vissza.</span>,
            <span key={`b${k}`}>Ami elmúlik, abból valami kinő.</span>,
            <span key={`c${k}`}>Helyi körforgás, zsákban.</span>,
            <span key={`d${k}`}>Helyben keletkezik. Helyben nő tovább.</span>,
          ])}
        </div>
      </div>

      {/* HOGYAN MŰKÖDIK */}
      <section id="hogyan-mukodik" className="section">
        <div className="container-gb">
          <div className="section-head split">
            <div>
              <p className="eyebrow">Hogyan működik?</p>
              <h2 className="h-lg">Mi az a GrowBag?</h2>
            </div>
            <p className="lead muted">A megfelelő növényi alapanyag komposztálással és érleléssel olyan komposztalapú anyaggá válhat, amely később a kertészetben hasznosítható. Ennyi az egész — csak helyben csináljuk.</p>
          </div>
          <ol className="flow">
            <li><span className="num">01</span><span className="label">Növényi alapanyag</span><span className="hint">Felhasználható, megfelelő növényi szerves anyag a környékről.</span></li>
            <li><span className="num">02</span><span className="label">Begyűjtés</span><span className="hint">Válogatva, hogy csak a megfelelő anyag kerüljön tovább.</span></li>
            <li><span className="num">03</span><span className="label">Komposztálás</span><span className="hint">Kontrollált lebomlás, idővel és odafigyeléssel.</span></li>
            <li><span className="num">04</span><span className="label">Érlelés</span><span className="hint">Az anyagnak idő kell, hogy kertben használható legyen.</span></li>
            <li className="is-product"><span className="num">05</span><span className="label">GrowBag</span><span className="hint">Komposztalapú termék, zsákba töltve.</span></li>
            <li><span className="num">06</span><span className="label">Helyi kert</span><span className="hint">Vissza oda, ahonnan elindult.</span></li>
          </ol>
          <p className="quote">A GrowBag célja egyszerű: amit helyben lehet hasznosítani, azt ne hagyjuk elveszni.</p>
        </div>
      </section>

      {/* MIÉRT FONTOS */}
      <section className="section section-dark">
        <div className="container-gb">
          <p className="eyebrow">Miért fontos?</p>
          <h2 className="h-lg" style={{ maxWidth: "13em" }}>Miért fontos, hogy ne csak kidobjuk?</h2>
          <div className="compare">
            <div className="lane">
              <div className="lane-head"><span className="mono" style={{ color: "var(--muted-on-dark)" }}>A megszokott út</span></div>
              <div className="lane-steps">
                <span className="lane-step">Növényi anyag</span>
                <span className="lane-arrow">→</span>
                <span className="lane-step">Hulladék</span>
                <span className="lane-arrow">→</span>
                <span className="lane-step end-dead">Eltűnik</span>
              </div>
              <p className="lane-verdict">Összekeveredik más hulladékkal, és nem kerül vissza oda, ahol keletkezett.</p>
            </div>
            <div className="vs">VS.</div>
            <div className="lane is-loop">
              <div className="lane-head"><span className="mono" style={{ color: "var(--kraft)" }}>A GrowBag út</span></div>
              <div className="lane-steps">
                <span className="lane-step">Növényi anyag</span>
                <span className="lane-arrow">→</span>
                <span className="lane-step">Komposzt</span>
                <span className="lane-arrow">→</span>
                <span className="lane-step">GrowBag</span>
                <span className="lane-arrow">→</span>
                <span className="lane-step end-loop">Kert</span>
                <span className="lane-loop">↺</span>
              </div>
              <p className="lane-verdict"><strong>Körforgásban marad:</strong> helyben hasznosul, és újra a kertekbe kerül.</p>
            </div>
          </div>
          <div className="points">
            <p>A növényi szerves anyag nem feltétlenül szemét. Megfelelő kezelés mellett értékes szerves anyag lehet belőle.</p>
            <p>Ha megfelelő növényi anyag a vegyes hulladékáramba kerül, összekeveredik más hulladékokkal, ezért sokkal nehezebb külön kezelni és hasznosítani.</p>
            <p>A GrowBag célja, hogy a megfelelő növényi alapanyag helyi körforgásban maradjon.</p>
          </div>
        </div>
      </section>

      {/* GYŰJTÉS */}
      <section id="gyujtes" className="section section-paper2">
        <div className="container-gb">
          <div className="section-head split">
            <div>
              <p className="eyebrow">Gyűjtés</p>
              <h2 className="h-lg">Gyűjtsd külön. Mi elvisszük.</h2>
            </div>
            <p className="lead muted">Ha eddig a növényi maradék is a vegyes kukába ment, most egyszerűbb külön tenni. A GrowBag összegyűjti, és díjmentesen elszállítja tőled.</p>
          </div>
          <div className="collect-grid">
            <div className="collect-card"><h3>Kevesebb megy a vegyes kukába</h3><p>A konyhában és a kertben keletkező növényi maradék jelentős része lehet a háztartási hulladéknak. Ha külön gyűjtöd, nem keveredik a többi szeméttel.</p></div>
            <div className="collect-card"><h3>Nem kell saját komposztáló</h3><p>Nem kell helyet, időt és figyelmet szánnod a komposztálásra. Te csak külön teszed, a kezelést és az érlelést mi végezzük.</p></div>
            <div className="collect-card"><h3>Visszakerül a környékre</h3><p>Amit összegyűjtünk, abból GrowBag lesz, és a XVII. kerület kertjeibe kerül vissza.</p></div>
            <div className="collect-card is-free">
              <span className="mono">Díjmentes</span>
              <h3>Összegyűjtjük és elszállítjuk</h3>
              <p>A begyűjtésért és az elszállításért nem kérünk pénzt. Lentebb leírjuk, pontosan milyen növényi maradék jöhet.</p>
              <a href="#gyujts-velunk" onClick={(e) => { e.preventDefault(); scrollTo("gyujts-velunk"); }} className="btn btn-primary" style={{ marginTop: "auto" }} data-testid="collect-cta-button">Gyűjtenék <span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </section>

      {/* GYŰJTS VELÜNK */}
      <section id="gyujts-velunk" className="section section-dark">
        <div className="container-gb signup-grid">
          <div>
            <p className="eyebrow">Gyűjts velünk</p>
            <h2 className="h-lg" style={{ marginBottom: 24 }}>Te gyűjtöd. Mi jövünk érte. Ingyen.</h2>
            <p className="lead muted">Vannak munkatársaink, akik szívesen összeszedik tőled a felhalmozódott növényi hulladékot – teljesen díjmentesen. Csak tedd külön, és írj nekünk. Mi egyből látjuk, és jelentkezünk.</p>
            <div className="collect-lists">
              <div className="collect-list ok" data-testid="collect-list-yes">
                <span className="mono">Ezt gyűjtsd</span>
                <ul>
                  <li>Zöldség- és gyümölcshéj, -maradék</li>
                  <li>Tojáshéj</li>
                  <li>Kávézacc, teafilter (papír)</li>
                  <li>Fűnyesedék</li>
                  <li>Elszáradt virág, lágyszárú gaz</li>
                  <li>Apró gally, nyesedék</li>
                </ul>
              </div>
              <div className="collect-list no" data-testid="collect-list-no">
                <span className="mono">Ezt ne</span>
                <ul>
                  <li>Hús, hal, csont, tejtermék</li>
                  <li>Főtt, olajos, sós étel</li>
                  <li>Műanyag, üveg, fém, zacskó</li>
                  <li>Avar, lehullott levél (bogarak miatt)</li>
                  <li>Macska-, kutyaalom</li>
                  <li>Beteg vagy vegyszerezett növény</li>
                </ul>
              </div>
            </div>
            <div className="ticket" style={{ marginTop: 28 }}>
              <div className="t-pct" style={{ fontSize: "1.6rem" }}>0 Ft</div>
              <div className="t-txt"><b>Elszállítás díjmentesen</b><span>XVII. kerület és környéke · egyeztetett időpontban</span></div>
            </div>
          </div>
          <PickupFormGB />
        </div>
      </section>

      {/* TÖRTÉNET */}
      <section className="section">
        <div className="container-gb">
          <div className="story-grid">
            <div>
              <p className="eyebrow">Történetünk</p>
              <h2 className="h-md" style={{ marginBottom: 32 }}>XVII. kerület → GrowBag → XVII. kerület</h2>
              <div>
                <p className="story-q">Egy kis ötlet a XVII. kerületből.</p>
                <p>Az egész egy egyszerű kérdéssel kezdődött: mi lenne, ha a környéken keletkező növényi szerves anyag nem egyszerűen eltűnne a hulladékáramban?</p>
                <p>Mi lenne, ha megfelelő kezelés után ugyanoda kerülhetne vissza, ahol keletkezett?</p>
                <p><strong>Így született meg a GrowBag ötlete.</strong></p>
              </div>
            </div>
            <svg className="loop" viewBox="-75 0 550 400">
              <g className="spin"><circle cx="200" cy="200" r="184" fill="none" stroke="#15120e" strokeOpacity=".25" strokeDasharray="2 7" /></g>
              <circle cx="200" cy="200" r="140" fill="none" stroke="#15120e" strokeWidth="1.5" />
              <text x="200" y="196" textAnchor="middle" fontSize="44" fontWeight="800" fill="#15120e" style={{ fontFamily: "var(--font-display)" }}>XVII.</text>
              <text x="200" y="222" textAnchor="middle" fontSize="10" letterSpacing="2.4" fill="#6c5e50" style={{ fontFamily: "var(--font-mono)" }}>HELYI KÖRFORGÁS</text>
              <g>
                <circle cx="200" cy="60" r="11" fill="#15120e" stroke="#f2ede3" strokeWidth="4" />
                <text x="200" y="22" textAnchor="middle" fontSize="11.5" fontWeight="600" letterSpacing="1.6" fill="#15120e" style={{ fontFamily: "var(--font-mono)" }}>XVII. KERÜLET</text>
                <text x="200" y="37" textAnchor="middle" fontSize="9.5" letterSpacing="1" fill="#6c5e50" style={{ fontFamily: "var(--font-mono)" }}>NÖVÉNYI ANYAG</text>
              </g>
              <g>
                <circle cx="321.24" cy="270" r="11" fill="#c49a6c" stroke="#f2ede3" strokeWidth="4" />
                <text x="341" y="272" fontSize="11.5" fontWeight="600" letterSpacing="1.6" fill="#15120e" style={{ fontFamily: "var(--font-mono)" }}>GROWBAG</text>
                <text x="341" y="287" fontSize="9.5" letterSpacing="1" fill="#6c5e50" style={{ fontFamily: "var(--font-mono)" }}>KOMPOSZT · ÉRLELÉS</text>
              </g>
              <g>
                <circle cx="78.76" cy="270" r="11" fill="#15120e" stroke="#f2ede3" strokeWidth="4" />
                <text x="59" y="272" textAnchor="end" fontSize="11.5" fontWeight="600" letterSpacing="1.6" fill="#15120e" style={{ fontFamily: "var(--font-mono)" }}>HELYI KERT</text>
                <text x="59" y="287" textAnchor="end" fontSize="9.5" letterSpacing="1" fill="#6c5e50" style={{ fontFamily: "var(--font-mono)" }}>VISSZA A FÖLDBE</text>
              </g>
            </svg>
          </div>
          <p className="mantra">
            <span>Helyben keletkezik.</span>
            <span>Helyben készül.</span>
            <span>Helyben nő tovább.</span>
          </p>
        </div>
      </section>

      {/* KAMPÁNYKÁRTYA */}
      <section className="section section-kraft">
        <div className="container-gb card-grid">
          <div className="seed-card-stage">
            <div className="seed-card" aria-hidden>
              <div className="sc-top">GROWBAG</div>
              <div className="sc-msg">Ne dobd ki.<br />Növeszd vissza.</div>
              <div className="sc-bottom">
                <div className="sc-note">Újrahasznosított papír<br />magokkal<br />XVII. ker. · 001</div>
                <div className="qr-box">QR<br />KÓD</div>
              </div>
            </div>
          </div>
          <div>
            <p className="eyebrow" style={{ color: "var(--soil)" }}>A kártya</p>
            <h2 className="h-lg" style={{ marginBottom: 28 }}>Még a meghívó is új életet kap.</h2>
            <p>A GrowBag első kampánykártyái sem teljesen új papírból készülnek.</p>
            <p>Régi, újrahasznosításra szánt papírból készítjük el a lapokat, majd növénymagokat helyezünk beléjük.</p>
            <p>Megfelelő körülmények között a papír lebomolhat, a magok pedig kicsírázhatnak.</p>
            <div className="chain">
              <span className="c">Régi papír</span><span className="a">→</span>
              <span className="c">Új papírlap</span><span className="a">→</span>
              <span className="c">Mag</span><span className="a">→</span>
              <span className="c">Föld</span><span className="a">→</span>
              <span className="c">Növény</span>
            </div>
            <p className="fineprint">A csírázás nem garantált: függ a magtól, a földtől, az időjárástól és a gondozástól. Mindig a kártyán szereplő instrukciót kövesd.</p>
          </div>
        </div>
      </section>

      {/* TERMÉK */}
      <section id="growbag" className="section section-paper2">
        <div className="container-gb">
          <div className="section-head split">
            <div>
              <p className="eyebrow">GrowBag</p>
              <h2 className="h-lg">Az első GrowBag még készül.</h2>
            </div>
            <p className="lead muted">Az első GrowBag termék helyi növényi alapanyagból készülő, komposztalapú termék lesz. Az alábbiak tervezett kiszerelések — ár és elérhetőség akkor lesz, amikor az első adag kész.</p>
          </div>
          <div className="products">
            {[
              { kg: "5 KG", h: 69, w: 87, title: "GrowBag 5 kg", desc: "Balkonládába, cserepes növényekhez, kipróbálásra." },
              { kg: "10 KG", h: 93, w: 92, title: "GrowBag 10 kg", desc: "Magaságyásba, kisebb kerti munkákhoz." },
              { kg: "20 KG", h: 123, w: 99, title: "GrowBag 20 kg", desc: "Kerti ágyásokhoz, ültetéshez, tavaszi indításhoz." },
              { kg: "NAGY KISZERELÉS", h: 150, w: 105, title: "GrowBag nagy kiszerelés", desc: "Nagyobb kertekhez, közösségi kertekhez." },
            ].map((p) => (
              <article key={p.title} className="product">
                <div className="product-vis">
                  <div className="mini-bag" style={{ width: p.w, height: p.h }}><span>{p.kg}</span></div>
                </div>
                <span className="tag">Tervezett kiszerelés</span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <div className="meta"><span>Ár: később</span><span>Még nem rendelhető</span></div>
              </article>
            ))}
          </div>
          <div className="pro-row">
            <b>Professzionális mennyiségek</b>
            <span className="muted">Kertészeteknek, közösségi kerteknek, intézményeknek — később. Ha érdekel, jelöld a „10+ zsák" opciót.</span>
          </div>
        </div>
      </section>

      {/* FELIRATKOZÁS */}
      <section id="feliratkozas" className="section section-dark">
        <div className="container-gb signup-grid">
          <div>
            <p className="eyebrow">Legyél az elsők között</p>
            <h2 className="h-lg" style={{ marginBottom: 24 }}>Szeretnél az elsők között lenni? 🌱</h2>
            <p className="lead" style={{ color: "var(--muted-on-dark)" }}>Az első GrowBag adag még készül. Iratkozz fel, és értesítünk, amikor elérhető lesz.</p>
            <div className="ticket">
              <div className="t-pct">10%</div>
              <div className="t-txt">
                <b>Az első vásárlásodra 10% kedvezményt kapsz.</b>
                <span>Saját, egyedi kód — azonnal megkapod. Barátod ajánlásával: 15%.</span>
              </div>
            </div>
          </div>
          <SubscribeFormGB />
        </div>
      </section>
    </>
  );
}
